import NumberInput from '../../components/NumberInput';
import { Form, Header, Icon, Label, Message, Table } from '../../components';
import { formPropTypes } from '../../components/common';
import { formatDate } from '../../common';

import React, { Fragment } from 'react';
import { map, sum, uniq } from 'lodash';

// TODO: Scale readout next to bodyweight!
// TODO: Make this appear like the top of the Athletes' Card?

// TODO: Move the warn dialog on the right side of bodyweight. maybe after entry too?;

const WeighInForm = ({ formProps, values, onInputChange, meta }) => {
  const isExcluded = !!values.isExcluded;
  const onExcludedChange = (event, { checked, ...rest }) =>
    onInputChange(event, { value: !!checked, ...rest });

  const { bodyweight } = values;
  const bodyweightErrors = [];
  for (const contest of meta.entry.contests) {
    const { bodyweightMinimum, bodyweightMaximum } = contest.bodyweightCategory;

    if (bodyweightMinimum && bodyweight < bodyweightMinimum) {
      bodyweightErrors.push({
        key: `${contest.id}-light`,
        header: 'Athlete is too light',
        content: ['Athlete is underweight for contest ', contest.title],
      });
    }
    if (bodyweightMaximum && bodyweight > bodyweightMaximum) {
      bodyweightErrors.push({
        key: `${contest.id}-heavy`,
        header: 'Athlete is too heavy',
        content: ['Athlete is overweight for contest ', contest.title],
      });
    }
  }
  const bodyweightViolation = !!bodyweight && !!bodyweightErrors.length;

  const { total, totalMinimum } = meta.entry;
  const entryTotal = total || totalMinimum || 0;
  const declaredWeights = meta.lifts.map(
    lift => Number.parseInt(values[lift.uuid], 10) || 0,
  );
  const isTotalComplete = declaredWeights.every(Boolean);
  const startingTotal = sum(declaredWeights);
  const difference = startingTotal - entryTotal;
  const entryTotalViolation = isTotalComplete && difference < -20;

  const entryTotalText = total || (totalMinimum ? `(${totalMinimum})` : '');
  const differenceText = isTotalComplete
    ? difference > 0
      ? `+${difference}`
      : difference
    : '';
  return (
    <Form
      {...formProps}
      error={!isExcluded && (entryTotalViolation || bodyweightViolation)}
    >
      <Table fixed singleLine basic="very">
        <Table.Body>
          <Table.Row>
            <Table.HeaderCell width={6}>Lot number:</Table.HeaderCell>
            <Table.Cell width={12}>{meta.entry.lotNumber || '--'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Team:</Table.HeaderCell>
            <Table.Cell>
              {(meta.entry.teams.length
                ? map(meta.entry.teams, 'name')
                : ['--']
              ).join(', ')}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Family name:</Table.HeaderCell>
            <Table.Cell>{meta.entry.familyName || '--'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Given name:</Table.HeaderCell>
            <Table.Cell>{meta.entry.givenName || '--'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Date of birth:</Table.HeaderCell>
            <Table.Cell>
              {meta.entry.dateOfBirth
                ? formatDate(meta.entry.dateOfBirth, { width: 'fixed' })
                : '--'}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Age group:</Table.HeaderCell>
            <Table.Cell>
              {(meta.entry.contests.length
                ? uniq(map(meta.entry.contests, 'ageGroup.title'))
                : ['--']
              ).map(title => (
                <Label key={title} content={title} />
              ))}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell>Bodyweight category:</Table.HeaderCell>
            <Table.Cell>
              {(meta.entry.contests.length
                ? uniq(map(meta.entry.contests, 'bodyweightCategory.title'))
                : ['--']
              ).map(title => (
                <Label key={title} content={title} />
              ))}
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <Form.Group>
        <Form.Checkbox
          label="Exclude athlete from competition"
          name="isExcluded"
          checked={isExcluded}
          onChange={onExcludedChange}
        />
      </Form.Group>
      <Form.Group widths="three">
        <Form.Field
          control={NumberInput}
          precision={2}
          disabled={isExcluded}
          label="Bodyweight"
          error={bodyweightViolation}
          name="bodyweight"
          value={values.bodyweight}
          required
          onChange={onInputChange}
          labelPosition="right"
          placeholder="Bodyweight"
        >
          <input />
          <Label basic>kg</Label>
        </Form.Field>
      </Form.Group>
      {bodyweightErrors.map(message => (
        <Message icon="warning sign" error {...message} />
      ))}
      <Header disabled={isExcluded} size="small">
        Starting declarations:
      </Header>
      <Form.Group widths="three">
        {meta.lifts.map(lift => (
          <Form.Input
            key={lift.id}
            disabled={isExcluded}
            label={lift.title}
            name={lift.uuid}
            value={values[lift.uuid] || ''}
            onChange={onInputChange}
            labelPosition="right"
            placeholder={lift.title}
            required
            fluid
          >
            <input />
            <Label basic>kg</Label>
          </Form.Input>
        ))}
        <Form.Input
          label="Starting total"
          readOnly
          disabled={isExcluded}
          error={entryTotalViolation}
          iconPosition="left"
          labelPosition="right"
          value={isTotalComplete ? startingTotal : ''}
        >
          <Icon name="arrow right" />
          <input />
          <Label basic>kg</Label>
        </Form.Input>
      </Form.Group>
      {!!entryTotal && (
        <Fragment>
          <Header disabled={isExcluded} size="small">
            20 kg rule:
          </Header>
          <Form.Group widths="three">
            <Form.Input
              label="Entry total"
              readOnly
              disabled={isExcluded}
              iconPosition="left"
              labelPosition="right"
              value={entryTotalText}
            >
              <Icon name="lock" />
              <input />
              <Label basic>kg</Label>
            </Form.Input>
            <Form.Input
              label="Difference"
              readOnly
              disabled={isExcluded}
              error={entryTotalViolation}
              iconPosition="left"
              labelPosition="right"
              value={differenceText}
            >
              <Icon name="arrow right" />
              <input />
              <Label basic>kg</Label>
            </Form.Input>
          </Form.Group>
          {entryTotalViolation && (
            <Message
              error
              icon="warning sign"
              header="20 kg Rule (TCRR 6.6.5) violation"
              content={[
                'Starting total is ',
                Math.abs(difference),
                ' kg under the entry total.',
              ]}
            />
          )}
        </Fragment>
      )}
      {/* TODO: 20 kg rule.*/
      /*
    <Header size="small">20 kg rule:</Header>
    <Form.Group widths="equal">
      <Form.Field>
        <label>Entry total</label>
        <Input disabled>{row.total}</Input>
      </Form.Field>
      <Form.Field>
        <label>Start total</label>
        <Input disabled value={values.snatch + values.cleanAndJerk} />
      </Form.Field>
      <Form.Field>
        <label>Difference</label>
        <Input disabled value={-20} />
      </Form.Field>
    </Form.Group>
    */}
    </Form>
  );
};

WeighInForm.propTypes = formPropTypes;

export default WeighInForm;



// WEBPACK FOOTER //
// ./src/pages/WeighIn/WeighInForm.js